import { z } from 'zod';

// RFC 0002 §2 (Fase 3, en preparación) — 'SENT' separa "aceptado por el
// proveedor" de 'DELIVERED' ("confirmado por webhook"). El backend todavía
// no lo emite, pero el Zod del BO tiene que aceptarlo desde ya: si Fase 3
// se despliega antes que este cambio de BO, la lista rechazaría cualquier
// notificación en ese estado.
export const NotificationStatus = z.enum([
  'PENDING',
  'QUEUED',
  'PROCESSING',
  'SENT',
  'DELIVERED',
  'FAILED',
  'CANCELLED',
  'OPTED_OUT',
  'REJECTED',
  'RETRY_SCHEDULED',
]);
export type NotificationStatus = z.infer<typeof NotificationStatus>;

export const Channel = z.enum(['EMAIL', 'SMS', 'WHATSAPP', 'PUSH', 'IN_APP', 'WEBHOOK']);
export type Channel = z.infer<typeof Channel>;

export const Priority = z.enum(['HIGH', 'NORMAL', 'LOW']);
export type Priority = z.infer<typeof Priority>;

// RFC 0002 §2 (Fase 1) — categorías de error del proveedor. isTransient se
// deriva en el backend de 'transient' | 'quota'.
export const ErrorCategory = z.enum([
  'transient',
  'quota',
  'config',
  'recipient',
  'permanent',
  'bounce',
  'complaint',
]);
export type ErrorCategory = z.infer<typeof ErrorCategory>;

export const deliveryAttemptSchema = z.object({
  id: z.string(),
  attempt: z.number(),
  provider: z.string(),
  status: z.string(),
  sentAt: z.string().nullable(),
  errorMessage: z.string().nullable().optional(),
  createdAt: z.string(),
  // Nombre crudo del error del proveedor (p. ej. 'validation_error').
  providerErrorCode: z.string().nullable().optional(),
  // Código genérico de noten (p. ej. 'SERVICE_UNAVAILABLE').
  errorCode: z.string().nullable().optional(),
  // Respuesta cruda saneada del proveedor — también presente en éxitos (id, statusCode).
  providerResponse: z.record(z.string(), z.unknown()).nullable().optional(),
  errorCategory: ErrorCategory.nullable().optional(),
  // 'auto:transient' | 'auto:quota' | 'manual:<userId>' | 'sweeper:stuck'
  retryReason: z.string().nullable().optional(),
  providerMsgId: z.string().nullable().optional(),
  nextRetryAt: z.string().nullable().optional(),
});
export type DeliveryAttempt = z.infer<typeof deliveryAttemptSchema>;

export const notificationSchema = z.object({
  id: z.string(),
  status: NotificationStatus,
  channel: Channel,
  eventType: z.string(),
  to: z.string(),
  userId: z.string().nullable().optional(),
  tenantId: z.string().nullable().optional(),
  idempotencyKey: z.string(),
  variables: z.record(z.string(), z.unknown()).nullable().optional(),
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
  priority: Priority.optional(),
  scheduledAt: z.string().nullable().optional(),
  subject: z.string().nullable().optional(),
  deliveryAttempts: z.array(deliveryAttemptSchema).optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  lastErrorCode: z.string().nullable().optional(),
  lastErrorCategory: ErrorCategory.nullable().optional(),
  lastAttemptAt: z.string().nullable().optional(),
  attemptsCount: z.number(),
  nextRetryAt: z.string().nullable().optional(),
});
export type Notification = z.infer<typeof notificationSchema>;

export const notificationListSchema = z.object({
  id: z.string(),
  status: NotificationStatus,
  channel: Channel,
  eventType: z.string(),
  to: z.string(),
  createdAt: z.string(),
  lastErrorCode: z.string().nullable().optional(),
  lastErrorCategory: ErrorCategory.nullable().optional(),
  lastAttemptAt: z.string().nullable().optional(),
  attemptsCount: z.number(),
  nextRetryAt: z.string().nullable().optional(),
});
export type NotificationListItem = z.infer<typeof notificationListSchema>;
